import { useState, useEffect } from 'react';
import { isEmailVerified } from '../../../utils/firebase.utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { showAuthError } from '../../../utils/toast';
import ToastService from '../../../utils/toast';
import customVerificationService from '../../../services/customVerificationService';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);

  useEffect(() => {
    // If user is already verified, redirect to success page
    if (user && isEmailVerified()) {
      navigate('/email-verification-success');
    }
  }, [user, navigate]);

  const handleResendVerification = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const response = await customVerificationService.sendVerificationEmail();
      if (response.success) {
        ToastService.showSuccess('Verification email sent! Please check your inbox.');
      } else {
        showAuthError('Failed to send verification email. Please try again.');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      showAuthError('Failed to send verification email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    if (isCheckingVerification) return;
    
    setIsCheckingVerification(true);
    try {
      // Check custom verification status
      const response = await customVerificationService.getVerificationStatus();
      
      if (response.success && response.user_verified) {
        ToastService.showSuccess('Email verified successfully!');
        navigate('/email-verification-success');
      } else {
        ToastService.showWarning('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      showAuthError('Failed to check verification status. Please try again.');
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleSkipForNow = () => {
    // Allow user to continue without verification (they can verify later)
    navigate('/');
  };

  if (!user) {
    return null; // This page should only be shown to authenticated users
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">
              <i className="fas fa-envelope" />
            </span>
          </div>
          
          <h1 className="text-2xl font-bold mb-4 font-inter">
            Verify Your Email
          </h1>
          
          <p className="text-gray-600 mb-6">
            We've sent a verification email to <strong>{user.email}</strong>
          </p>
          
          <p className="text-sm text-gray-500 mb-8">
            Please click the link in the email to verify your account. This helps us keep your account secure.
          </p>
          
          <div className="space-y-4">
            <button
              onClick={handleCheckVerification}
              disabled={isCheckingVerification}
              className={`w-full ${isCheckingVerification 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
              } text-white font-bold text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all`}
            >
              {isCheckingVerification ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Checking...</span>
                </>
              ) : (
                <span>I've Verified My Email</span>
              )}
            </button>
            
            <button
              onClick={handleResendVerification}
              disabled={isLoading}
              className={`w-full ${isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-white hover:bg-gray-50 border border-gray-300'
              } text-gray-700 font-medium text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <span>Resend Verification Email</span>
              )}
            </button>
            
            <button
              onClick={handleSkipForNow}
              className="w-full text-gray-500 hover:text-gray-700 text-sm transition-colors py-2"
              disabled={isLoading || isCheckingVerification}
            >
              Skip for now (verify later)
            </button>
          </div>
          
          <div className="mt-8 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-yellow-800">
              <strong>Didn't receive the email?</strong><br />
              Check your spam folder or try resending the verification email.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;